import type { Selection } from "@/components/playbook-selector";
import { BASE, authFetch, request } from "./client";

export type SessionMode = "text2text" | "text2audio" | "audio2audio";

export interface CreateSessionRequest {
  playbookId?: string;
  agentBackendId?: string;
  mode?: SessionMode;
  source?: string;
  params?: Record<string, string>;
}

export interface CreateSessionResponse {
  session_id: string;
  reply: string;
  duration_ms: number;
  audio_base64: string | null;
  cache_hit?: string | null;
}

export function createSession(req: CreateSessionRequest): Promise<CreateSessionResponse> {
  const body: Record<string, unknown> = {};
  if (req.agentBackendId) {
    body.agent_backend_id = req.agentBackendId;
  } else if (req.playbookId) {
    body.playbook = req.playbookId;
  }
  if (req.mode) body.mode = req.mode;
  if (req.source) body.source = req.source;
  if (req.params && Object.keys(req.params).length > 0) body.params = req.params;
  return request("/session", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export function createSessionFromSelection(
  selection: Selection,
  opts: { source: string; mode?: SessionMode; params?: Record<string, string> },
): Promise<CreateSessionResponse> {
  if (selection.type === "agent_backend") {
    return createSession({ agentBackendId: selection.id, mode: opts.mode, source: opts.source });
  }
  return createSession({ playbookId: selection.id, mode: opts.mode, source: opts.source, params: opts.params });
}

export function dumpSession(sessionId: string): Promise<Record<string, unknown>> {
  return request(`/session/${sessionId}/dump`);
}

export interface DimensionScore {
  score: number;
  detail: string;
}

export interface Evaluation {
  id: number;
  session_id: string;
  created_at: number;
  overall: number;
  suggestions: string[];
  dimensions: Record<string, DimensionScore>;
}

export interface SessionRecord {
  session_id: string;
  playbook_id: string;
  voice_id: string;
  source: string;
  created_at: number;
  ended_at: number | null;
  turn_count: number;
  duration_ms: number;
  greeting: string;
  tags: string | null;
  eval_score?: number | null;
  success?: number | null;
  reject_reason?: string | null;
  end_reason?: string | null;
  answered?: boolean | null;
  hangup_by?: string | null;
  batch_id?: string | null;
  call_group_id?: string | null;
  slot_completion_score?: number | null;
  compliance_score?: number | null;
  naturalness_score?: number | null;
  objection_handling_score?: number | null;
  goal_achievement_score?: number | null;
  topic_convergence_score?: number | null;
  audited_at?: number | null;
  call_end_state?: string | null;
  amd_detected?: number | null;
  phone?: string | null;
  intent_score?: number | null;
  intent_directions?: string | null;
  intent_at?: number | null;
}

export interface SessionDetail extends SessionRecord {
  history: Array<{ role: string; content: string; debug?: Record<string, unknown>; audio_url?: string }>;
  has_audio?: boolean;
  evaluation?: Evaluation | null;
}

export interface SessionListResponse {
  sessions: SessionRecord[];
  limit: number;
  offset: number;
  total: number;
}

export type AuditStatus = "" | "unaudited" | "audited";

export interface ListSessionsParams {
  limit?: number;
  offset?: number;
  source?: string;
  playbook_id?: string;
  tag?: string;
  tags?: string[];
  audit_status?: AuditStatus;
  amd_filter?: "only" | "exclude";
  batch_id?: string;
  call_group_id?: string;
  start_date?: number;
  end_date?: number;
  outcome?: string;
  ended_only?: boolean;
  min_turns?: number;
  min_overall?: number;
  min_goal?: number;
  min_intent?: number;
  intent_directions?: string[];
  phone?: string;
  min_duration?: number;
}

export type SessionFilters = Omit<ListSessionsParams, "limit" | "offset">;

function applySessionFilters(params: URLSearchParams, f: SessionFilters): void {
  if (f.source) params.set("source", f.source);
  if (f.playbook_id) params.set("playbook_id", f.playbook_id);
  if (f.tag) params.set("tag", f.tag);
  if (f.tags && f.tags.length > 0) params.set("tags", f.tags.join(","));
  if (f.audit_status) params.set("audit_status", f.audit_status);
  if (f.amd_filter) params.set("amd_filter", f.amd_filter);
  if (f.batch_id) params.set("batch_id", f.batch_id);
  if (f.call_group_id) params.set("call_group_id", f.call_group_id);
  if (f.start_date) params.set("start_date", String(f.start_date));
  if (f.end_date) params.set("end_date", String(f.end_date));
  if (f.outcome) params.set("outcome", f.outcome);
  if (f.ended_only) params.set("ended_only", "true");
  if (f.min_turns) params.set("min_turns", String(f.min_turns));
  if (f.min_overall && f.min_overall > 0) params.set("min_overall", String(f.min_overall));
  if (f.min_goal && f.min_goal > 0) params.set("min_goal", String(f.min_goal));
  if (f.min_intent && f.min_intent > 0) params.set("min_intent", String(f.min_intent));
  if (f.intent_directions && f.intent_directions.length > 0) {
    params.set("intent_directions", f.intent_directions.join(","));
  }
  if (f.phone) params.set("phone", f.phone);
  if (f.min_duration) params.set("min_duration", String(f.min_duration));
}

export function listSessions(p: ListSessionsParams = {}): Promise<SessionListResponse> {
  const params = new URLSearchParams({
    limit: String(p.limit ?? 50),
    offset: String(p.offset ?? 0),
  });
  applySessionFilters(params, p);
  return request(`/admin/sessions?${params}`);
}

export interface ExportPlaybookParams {
  start_date?: number;
  end_date?: number;
  outcome?: string;
  tag?: string;
  min_overall?: number;
  min_goal?: number;
  min_intent?: number;
  min_turns?: number;
  call_group_id?: string;
}

export function exportPlaybookSessionsUrl(playbookId: string, p: ExportPlaybookParams = {}): string {
  const params = new URLSearchParams();
  applySessionFilters(params, p);
  const qs = params.toString();
  return `${BASE}/playbooks/${encodeURIComponent(playbookId)}/export${qs ? `?${qs}` : ""}`;
}

export function getSessionDetail(id: string): Promise<SessionDetail> {
  return request(`/sessions/${id}`);
}

export async function getSessionNeighbor(
  id: string,
  direction: "prev" | "next",
  filters: SessionFilters,
): Promise<string | null> {
  const params = new URLSearchParams({ id, direction });
  applySessionFilters(params, filters);
  const data = await request<{ session_id: string | null }>(`/admin/sessions/neighbor?${params}`);
  return data.session_id;
}

export function evaluateSession(id: string): Promise<Evaluation> {
  return request(`/sessions/${id}/evaluate`, { method: "POST" });
}

export function classifyOutcome(id: string): Promise<{ success: boolean; reject_reason: string | null }> {
  return request(`/sessions/${id}/classify-outcome`, { method: "POST" });
}

export function retagSession(id: string): Promise<{ tags: string[] }> {
  return request(`/sessions/${id}/retag`, { method: "POST" });
}

export interface ReintentResult {
  intent_score: number | null;
  intent_directions: string[];
}

export function reintentSession(id: string): Promise<ReintentResult> {
  return request(`/sessions/${id}/reintent`, { method: "POST" });
}

export function listIntentDirections(playbookId: string): Promise<{ directions: string[] }> {
  return request(`/playbooks/${encodeURIComponent(playbookId)}/intent-directions`);
}

export function previewReintentMissing(
  playbookId: string,
  filters: RetagMissingFilters,
): Promise<{ missing_count: number }> {
  const params = new URLSearchParams();
  applySessionFilters(params, filters);
  const qs = params.toString();
  return request(`/playbooks/${encodeURIComponent(playbookId)}/reintent-missing/preview${qs ? `?${qs}` : ""}`);
}

export function startReintentMissingStream(
  playbookId: string,
  filters: RetagMissingFilters,
  signal: AbortSignal,
): Promise<Response> {
  const params = new URLSearchParams();
  applySessionFilters(params, filters);
  const qs = params.toString();
  return authFetch(`/playbooks/${encodeURIComponent(playbookId)}/reintent-missing${qs ? `?${qs}` : ""}`, {
    method: "POST",
    signal,
  });
}

export interface RetagMissingPreview {
  null_count: number;
  empty_count: number;
}

export type RetagMissingFilters = Pick<
  SessionFilters,
  "start_date" | "end_date" | "outcome" | "tags" | "min_overall" | "min_goal" | "min_turns" | "audit_status" | "amd_filter" | "call_group_id"
>;

export function previewRetagMissing(
  playbookId: string,
  filters: RetagMissingFilters,
): Promise<RetagMissingPreview> {
  const params = new URLSearchParams();
  applySessionFilters(params, filters);
  const qs = params.toString();
  return request(`/playbooks/${encodeURIComponent(playbookId)}/retag-missing/preview${qs ? `?${qs}` : ""}`);
}

export function startRetagMissingStream(
  playbookId: string,
  filters: RetagMissingFilters,
  includeEmpty: boolean,
  signal: AbortSignal,
): Promise<Response> {
  const params = new URLSearchParams();
  applySessionFilters(params, filters);
  if (includeEmpty) params.set("include_empty", "true");
  const qs = params.toString();
  return authFetch(`/playbooks/${encodeURIComponent(playbookId)}/retag-missing${qs ? `?${qs}` : ""}`, {
    method: "POST",
    signal,
  });
}

export function updateSessionTags(sessionId: string, tags: string[]): Promise<{ tags: string[] }> {
  return request(`/sessions/${sessionId}/tags`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ tags }),
  });
}

export function sessionAudioUrl(sessionId: string, turnIndex: number): string {
  return `${BASE}/sessions/${sessionId}/audio/${turnIndex}`;
}

export function sessionRecordingUrl(
  sessionId: string,
  track?: "user" | "ai",
): string {
  const base = `${BASE}/sessions/${sessionId}/recording`;
  return track ? `${base}?track=${track}` : base;
}
